import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { PageHeader } from "../../components/cards/PageHeader";
import { getBookingInvoicesRequest } from "../../api/invoiceApi";
import { BookingInvoiceEmailResponse } from "../../models/ResponseModels/BookingInvoiceEmailResponse";
import { useTranslation } from "../../translations/i18n";
import { formatMoney } from "../../utils/helperFunctions/currencyFormatter";
import { formatShortDisplayDate } from "../../utils/helperFunctions/dateFormatter";

const getStatusColor = (
  status: BookingInvoiceEmailResponse["status"],
): "default" | "success" | "warning" | "error" => {
  if (status === "sent") {
    return "success";
  }

  if (status === "pending") {
    return "warning";
  }

  if (status === "failed") {
    return "error";
  }

  return "default";
};

const InvoicesScreen = () => {
  const { t } = useTranslation();

  const [invoices, setInvoices] = useState<BookingInvoiceEmailResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getBookingInvoicesRequest({ per_page: 50 });
      setInvoices(response.data);
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message || t("invoices.failedLoad"),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((first, second) => {
      const firstDate = new Date(
        first.sent_at || first.created_at || 0,
      ).getTime();
      const secondDate = new Date(
        second.sent_at || second.created_at || 0,
      ).getTime();

      return secondDate - firstDate;
    });
  }, [invoices]);

  return (
    <Box>
      <PageHeader
        title={t("invoices.title")}
        description={t("invoices.description")}
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={loadInvoices}
            disabled={loading}
          >
            {t("common.refresh")}
          </Button>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading && invoices.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : sortedInvoices.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5} sx={{ py: 5, alignItems: "center" }}>
              <ReceiptLongRoundedIcon color="disabled" sx={{ fontSize: 48 }} />

              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {t("invoices.emptyTitle")}
              </Typography>

              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                {t("invoices.empty")}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {sortedInvoices.map((invoice) => {
            const booking = invoice.booking;
            const guestName = booking?.guest?.full_name || "-";
            const unitName = booking?.unit?.name || "-";
            const totalPrice = booking
              ? formatMoney(booking.total_price, booking.currency)
              : "-";

            return (
              <Card key={invoice.id}>
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", md: "center" },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          mb: 0.5,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>
                          {guestName}
                        </Typography>

                        <Chip
                          size="small"
                          color={getStatusColor(invoice.status)}
                          label={t(`invoices.status.${invoice.status}`)}
                        />
                      </Stack>

                      <Typography color="text.secondary">
                        {invoice.guest_email}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {t("booking.unit")}: {unitName}
                      </Typography>

                      {booking ? (
                        <Typography variant="body2" color="text.secondary">
                          {formatShortDisplayDate(booking.check_in)} -{" "}
                          {formatShortDisplayDate(booking.check_out)} ·{" "}
                          {totalPrice}
                        </Typography>
                      ) : null}

                      {invoice.error_message ? (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {invoice.error_message}
                        </Alert>
                      ) : null}
                    </Box>

                    <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("invoices.sentAt")}
                      </Typography>

                      <Typography sx={{ fontWeight: 900 }}>
                        {invoice.sent_at
                          ? formatShortDisplayDate(invoice.sent_at)
                          : "-"}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default InvoicesScreen;
